import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { progressRepository } from '@/storage/repository';
import { lastSession } from '@/training/lastSession';
import type { SessionSummary } from '@/types/training';

/** A stuck write must not trap the player on the exercise screen. */
const SAVE_TIMEOUT_MS = 3000;

/**
 * Finish handler shared by every training screen.
 *
 * The save is **awaited before navigating**. The completion screen reads the
 * database to build its "practise next" recommendation, so navigating first
 * meant it could render advice computed without the session that had just
 * ended — most visibly on a player's very first session, where it would still
 * claim there was no history.
 *
 * If the write hangs, we navigate anyway after a short timeout: the results are
 * already in memory, and losing one session's history is much better than
 * stranding someone on a finished exercise.
 */
export function useFinishSession(): (summary: SessionSummary) => void {
  const navigate = useNavigate();
  // Guards against a double finish (the duration elapsing as the player also
  // presses back) producing two saves and two navigations.
  const finished = useRef(false);

  return useCallback(
    (summary: SessionSummary) => {
      if (finished.current) return;
      finished.current = true;
      lastSession.set(summary);

      const save = progressRepository.saveSession(summary).catch((error: unknown) => {
        console.error('[storage] failed to save session', error);
      });
      const deadline = new Promise<void>((resolve) => setTimeout(resolve, SAVE_TIMEOUT_MS));

      void Promise.race([save, deadline]).then(() => {
        navigate('/session/complete', { replace: true });
      });
    },
    [navigate],
  );
}
